<template>
  <div class="life-care-settings-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <el-button @click="$router.push('/dashboard')" type="primary" plain style="margin-right: 10px;">
            返回首页
          </el-button>
          <span>生活照料内容设置</span>
          <el-button type="primary" @click="handleAdd">添加生活照料内容</el-button>
        </div>
      </template>
      
      <el-table :data="lifeCareList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="service_name" label="服务名称" width="180" />
        <el-table-column prop="description" label="描述" show-overflow-tooltip />
        <el-table-column prop="price" label="价格(元)" width="100" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="Number(scope.row.status) === 1 ? 'success' : 'danger'">
              {{ Number(scope.row.status) === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180" />
        <el-table-column prop="updated_at" label="更新时间" width="180" />
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button 
              :type="Number(scope.row.status) === 1 ? 'danger' : 'success'" 
              size="small" 
              @click="handleStatusChange(scope.row)"
            >
              {{ Number(scope.row.status) === 1 ? '禁用' : '启用' }}
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加/编辑对话框 -->
    <el-dialog 
      :title="dialogType === 'add' ? '添加生活照料内容' : '编辑生活照料内容'" 
      v-model="dialogVisible"
      width="500px"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="服务名称" prop="service_name">
          <el-input v-model="form.service_name" />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="价格(元)" prop="price">
          <el-input-number v-model="form.price" :precision="2" :step="0.1" :min="0" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

export default {
  name: 'LifeCareSettings',
  setup() {
    const loading = ref(false)
    const lifeCareList = ref([])
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const formRef = ref(null)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    
    const form = ref({
      service_name: '',
      description: '',
      price: 0,
      status: 1
    })
    
    const rules = {
      service_name: [{ required: true, message: '请输入服务名称', trigger: 'blur' }],
      description: [{ required: true, message: '请输入描述', trigger: 'blur' }],
      price: [{ required: true, message: '请输入价格', trigger: 'blur' }]
    }
    
    const fetchLifeCareList = async () => {
      loading.value = true
      try {
        const response = await fetch(`/api/life_care_settings_list.php?page=${currentPage.value}&pageSize=${pageSize.value}`)
        const data = await response.json()
        if (data.code === 0) {
          lifeCareList.value = data.data.list
          total.value = data.data.total
        } else {
          ElMessage.error(data.msg || '获取生活照料内容列表失败')
        }
      } catch (error) {
        console.error('获取生活照料内容列表失败:', error)
        ElMessage.error('获取生活照料内容列表失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleAdd = () => {
      form.value = {
        service_name: '',
        description: '',
        price: 0,
        status: 1
      }
      dialogType.value = 'add'
      dialogVisible.value = true
    }
    
    const handleEdit = (row) => {
      form.value = { ...row }
      dialogType.value = 'edit'
      dialogVisible.value = true
    }
    
    const handleStatusChange = async (row) => {
      try {
        const action = Number(row.status) === 1 ? '禁用' : '启用'
        await ElMessageBox.confirm(`确定要${action}该生活照料内容吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/update_life_care_status.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            status: Number(row.status) === 1 ? 0 : 1
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(`${action}成功`)
          fetchLifeCareList()
        } else {
          ElMessage.error(data.msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('更新状态失败:', error)
          ElMessage.error('更新状态失败')
        }
      }
    }
    
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm('确定要删除该生活照料内容吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await fetch('/api/life_care_settings_delete.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id
          })
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success('删除成功')
          fetchLifeCareList()
        } else {
          ElMessage.error(data.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }
    
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        const url = dialogType.value === 'add' ? '/api/life_care_settings_add.php' : '/api/life_care_settings_update.php'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(form.value)
        })
        
        const data = await response.json()
        if (data.code === 0) {
          ElMessage.success(dialogType.value === 'add' ? '添加成功' : '更新成功')
          dialogVisible.value = false
          fetchLifeCareList()
        } else {
          ElMessage.error(data.msg || (dialogType.value === 'add' ? '添加失败' : '更新失败'))
        }
      } catch (error) {
        console.error('提交失败:', error)
        ElMessage.error('提交失败')
      }
    }
    
    const handleSizeChange = (val) => {
      pageSize.value = val
      fetchLifeCareList()
    }
    
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchLifeCareList()
    }
    
    onMounted(() => {
      fetchLifeCareList()
    })
    
    return {
      loading,
      lifeCareList,
      dialogVisible,
      dialogType,
      formRef,
      form,
      rules,
      currentPage,
      pageSize,
      total,
      fetchLifeCareList,
      handleAdd,
      handleEdit,
      handleStatusChange,
      handleDelete,
      handleSubmit,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style scoped>
.life-care-settings-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style> 